import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { deepMerge } from '@miq/catalog';
import { isNotNull } from '@shared/functions';
import { DataObject } from '@shared/models/data-object.model';
import { AppStorageService, LOCALE_ID } from '@shared/services/app-storage.service';
import { L10nProvider, L10nTranslationLoader } from 'angular-l10n';
import { map, Observable, of, switchMap } from 'rxjs';
import { getLocalRegion } from './locale/locale-override.constants';

@Injectable()
export class HttpTranslationLoader implements L10nTranslationLoader {
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  constructor(
    private http: HttpClient,
    private storageService: AppStorageService
  ) {}

  public get(language: string, provider: L10nProvider): Observable<{ [key: string]: any }> {
    const url = `${provider.asset}-${language}.json`;
    return this.http
      .get(url, { headers: this.headers })
      .pipe(switchMap((localeData: DataObject) => this.getLocaleOverride(provider, localeData)));
  }

  /**
   * getLocaleOverride function to extract the name of the expected region
   *  that returns the overridden object from the JSON if it exists
   *
   * @param provider
   * @param localeData: object obtained from the main JSON
   * @returns an observable with the object of the original or overwritten translation
   */
  private getLocaleOverride(provider: L10nProvider, localeData: DataObject): Observable<DataObject> {
    const userLocalId = this.storageService.getItem<number>(LOCALE_ID);
    const region = getLocalRegion(userLocalId);
    const url = this.getUrlRequestLocaleOverride(provider, region);
    return isNotNull(region, url)
      ? this.http
          .get(url, { headers: this.headers })
          .pipe(map((localeDataOverride: DataObject) => deepMerge(localeData, localeDataOverride)))
      : of(localeData);
  }

  /**
   * getUrlRequestLocaleOverride function that returns the url to find
   * the JSON file that will overwrite the original
   *
   * @param provider
   * @param region: region according to user locale id
   * @returns an url
   */
  private getUrlRequestLocaleOverride(provider: L10nProvider, region: string): string | null {
    //If you need to add JSON override files for reports or apps, you must add the files in the corresponding assets and refactor this method.
    return provider.name === 'locale' || provider.name === 'app'
      ? `${provider.asset}-override/locale-${region}.json`
      : null;
  }
}
